import AttachFileIcon from '@mui/icons-material/AttachFile'
import CancelIcon from '@mui/icons-material/Cancel'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { Button } from '@mui/material'
import { useState } from 'react'
import { Accept, FileWithPath, useDropzone } from 'react-dropzone'
import styled from 'styled-components'

import GlobalThemeProvider from '../../style/GlobalTheme'
import { COLORS } from '../Colors'
import Tooltip from './Tooltip'

type FileUploaderComponentProps = {
    maxFiles?: number
    accept?: Accept
    onFilesSelected: (file: FileWithPath[]) => void
    onFileRemoved?: (fileName: string) => void
    fileNames?: string[]
    label: string
    tooltip?: string
}

export default function FileUploaderComponent({
    onFilesSelected,
    onFileRemoved,
    fileNames,
    maxFiles = 1,
    accept,
    label,
    tooltip,
}: FileUploaderComponentProps) {
    const [localFiles, setLocalFiles] = useState<FileWithPath[]>([])
    const files =
        localFiles.length > 0
            ? [...localFiles, ...(fileNames || []).map((name) => ({ name }))]
            : (fileNames || []).map((name) => ({ name }))

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: (droppedFiles) => {
            const duplicateFiles = droppedFiles.filter((file) =>
                files.some((existingFile) => existingFile.name === file.name)
            )

            if (duplicateFiles.length) return

            const newFiles = [...localFiles, ...droppedFiles]
            setLocalFiles(newFiles)
            onFilesSelected(newFiles)
        },
        maxFiles: maxFiles - localFiles.length,
        accept: accept || {
            'image/*': ['.png', '.jpg'],
            'application/xml': ['.xls', '.xlsx', '.csv'],
            'application/pdf': ['.pdf'],
        },
        maxSize: 10 * 1024 * 1024,
        disabled: files.length >= maxFiles,
    })

    const handleRemoveFile = (
        fileToRemove: string,
        event: React.MouseEvent
    ) => {
        event.stopPropagation()
        const updatedFiles = localFiles.filter(
            (file) => file.name !== fileToRemove
        )
        setLocalFiles(updatedFiles || [])
        onFilesSelected(updatedFiles || [])
        onFileRemoved?.(fileToRemove)
    }

    return (
        <GlobalThemeProvider>
            <HeaderContainer>
                <InputLabel>{label}</InputLabel>
                {tooltip && <Tooltip text={tooltip} />}
            </HeaderContainer>
            <DropzoneContainer
                {...getRootProps()}
                disabled={files.length >= maxFiles}
            >
                <input data-testid="dropzone-input" {...getInputProps()} />

                <DropzoneContentContainer>
                    {files.length < maxFiles && (
                        <>
                            <CloudUploadIcon color="primary" />
                            <GrayText>
                                Araste e solte os arquivos aqui ou
                            </GrayText>
                            <OrangeText>
                                {maxFiles === 1
                                    ? 'Selecione o arquivo...'
                                    : 'Selecione os arquivos...'}
                            </OrangeText>
                        </>
                    )}

                    {!!files.length && (
                        <DropzoneFullfiledContainer>
                            {files?.map((file) => (
                                <Button
                                    key={file.name}
                                    startIcon={<AttachFileIcon />}
                                    endIcon={
                                        <CancelIcon
                                            onClick={(e) =>
                                                handleRemoveFile(file.name, e)
                                            }
                                        />
                                    }
                                    style={{
                                        backgroundColor: COLORS.borderGray,
                                        color: COLORS.primaryGray,
                                        fontFamily: 'Roboto',
                                        fontSize: '12px',
                                        marginBottom: '8px',
                                    }}
                                    variant="contained"
                                >
                                    {file.name}
                                </Button>
                            ))}
                        </DropzoneFullfiledContainer>
                    )}
                </DropzoneContentContainer>
            </DropzoneContainer>
        </GlobalThemeProvider>
    )
}

const DropzoneContainer = styled.div<{ disabled?: boolean }>`
    display: flex;
    flex-direction: column;
    align-content: center;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    border: 2px solid ${COLORS.borderGray};
    border-radius: 3px;
    min-height: 110px;
    height: auto;
`
const DropzoneContentContainer = styled.div`
    padding: 15px;
    text-align: center;
    font-size: 12px;
    letter-spacing: 0.09px;
`

const DropzoneFullfiledContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px;
    align-items: flex-start;
    font-size: 12px;
    letter-spacing: 0.09px;
    gap: 8px;
`

const OrangeText = styled.p`
    color: ${COLORS.primaryOrange};
`
const GrayText = styled.p`
    color: ${COLORS.primaryGray};
`
const InputLabel = styled.span`
    flex-grow: 0;
    font-family: Roboto;
    font-size: 12px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 3;
    letter-spacing: 0.09px;
    text-align: left;
    color: ${COLORS.primaryGray};
    text-transform: uppercase;
`

const HeaderContainer = styled.div`
    display: flex;
    align-items: center;
`
